import React, {useEffect, useState} from "react";
import axios from "axios";
import {
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    SelectChangeEvent
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import {DataGrid, GridColDef, GridRowParams} from "@mui/x-data-grid";
import {GoogleAuthUser, RoleAssignment} from "../../../server/auth/GoogleAuth";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const AVAILABLE_ROLES = ['Admin', 'Owner', 'GM', 'Loremaster', 'Cast', 'Logistics', 'MonsterManager'];

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 480,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const columns: GridColDef[] = [
    {field: 'email', headerName: 'Email', flex: 2},
    {field: 'display_name', headerName: 'Display Name', flex: 1},
    {field: 'authorized', headerName: 'Authorized', width: 110, type: 'boolean'},
    {
        field: 'roles', headerName: 'Roles', flex: 2,
        renderCell: (params) =>
            (params.row.roles as RoleAssignment[])?.map(r => r.role).join(', ') ?? ''
    },
]

export default function AccessControl() {
    const [users, setUsers] = useState<GoogleAuthUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<GoogleAuthUser | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [newRole, setNewRole] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    function loadUsers() {
        axios.get('/admin/user').then(response => {
            setUsers(response.data);
        });
    }

    function openModal(user: GoogleAuthUser) {
        setSelectedUser(user);
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
        setSelectedUser(null);
        setNewRole('');
    }

    async function refreshAndSync(userId: string) {
        const updated: GoogleAuthUser[] = await axios.get('/admin/user').then(r => r.data);
        setUsers(updated);
        setSelectedUser(updated.find(u => u.id === userId) ?? null);
    }

    async function handleAddRole() {
        if (!selectedUser || !newRole) return;
        await axios.post(`/admin/user/${selectedUser.id}/role`, {role: newRole});
        setNewRole('');
        await refreshAndSync(selectedUser.id);
    }

    async function handleRemoveRole(role: RoleAssignment) {
        if (!selectedUser) return;
        await axios.delete(`/admin/user/${selectedUser.id}/role/${role.id}`);
        await refreshAndSync(selectedUser.id);
    }

    async function handleAuthorize() {
        if (!selectedUser) return;
        await axios.get(`/admin/user/${selectedUser.id}/authorize`);
        await refreshAndSync(selectedUser.id);
    }

    const assignedRoleNames = selectedUser?.roles.map(r => r.role) ?? [];
    const availableToAdd = AVAILABLE_ROLES.filter(r => !assignedRoleNames.includes(r));

    return <React.Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">Access Control</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <DataGrid
                        autoHeight
                        rows={users}
                        columns={columns}
                        density="compact"
                        onRowClick={(params: GridRowParams) => openModal(params.row as GoogleAuthUser)}
                        sx={{cursor: 'pointer'}}
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
        </Grid>

        <Modal open={modalOpen} onClose={closeModal}>
            <Box sx={modalStyle}>
                <Typography variant="h6" align="center" gutterBottom>
                    {selectedUser?.email}
                </Typography>
                {selectedUser?.display_name && (
                    <Typography variant="subtitle2" align="center" color="text.secondary" gutterBottom>
                        {selectedUser.display_name}
                    </Typography>
                )}

                {!selectedUser?.authorized && (
                    <Container sx={{textAlign: 'center', mb: 2}}>
                        <Button variant="outlined" startIcon={<PersonAddIcon/>} onClick={handleAuthorize}>
                            Authorize User
                        </Button>
                    </Container>
                )}

                <Typography variant="subtitle1" gutterBottom>Current Roles</Typography>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40, mb: 2}}>
                    {selectedUser?.roles.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No roles assigned</Typography>
                    )}
                    {selectedUser?.roles.map(role => (
                        <Chip
                            key={role.id}
                            label={role.role}
                            onDelete={() => handleRemoveRole(role)}
                        />
                    ))}
                </Box>

                <Typography variant="subtitle1" gutterBottom>Add Role</Typography>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={8}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="role-select-label">Role</InputLabel>
                            <Select
                                labelId="role-select-label"
                                label="Role"
                                value={newRole}
                                onChange={(ev: SelectChangeEvent) => setNewRole(ev.target.value)}
                            >
                                {availableToAdd.map(r => (
                                    <MenuItem key={r} value={r}>{r}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <Button fullWidth variant="contained" disabled={!newRole} onClick={handleAddRole}>
                            Add
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    </React.Fragment>
}
